"use client";

import React, { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  author: "JAMIE L.",
  quote: "\"His attention to detail is impeccable and his craftsmanship is exceptional. He goes above and beyond to ensure the work is done right.If you’re looking for a licensed general contractor who’s trustworthy, skilled, and genuinely puts his clients first, look no further.\"",
  position: "LAKEWOOD"
}, {
  author: "RYAN M.",
  quote: "\"The 3D modeling phase allowed us to see our dream remodel before a single nail was driven and the end result exceeded our expectations!\"",
  position: "GREEN MOUNTAIN"
}, {
  author: "SILVIA B.",
  quote: "\"We can’t say enough good things about BT Remodeling. Jon was God sent after a horrible experience with a previous contractor who left our project in shambles. From the moment we met we knew we were in great hands. He is professional, very knowledgeable about everything related to construction and he is committed to quality.\"",
  position: "LAKEWOOD"
}];
export default function ClientSuccessStories() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="222817" id="client-success-stories" className="bg-background py-24 sm:py-32 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-4 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-primary tracking-tighter uppercase">
            Client Success Stories
          </h2>
          <p className="text-muted-foreground max-w-2xl text-lg font-default">
            Hear from Denver homeowners who entrusted their vision to our master craftsmen and design experts.
          </p>
        </div>

        <div className="max-w-5xl mx-auto relative px-12 md:px-16">
          <Carousel opts={{
          align: "center",
          loop: true,
          duration: 60
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((testimonial, i) => <CarouselItem key={i} data-index={i} className="md:basis-full lg:basis-full">
                  <div className="p-2 md:p-6 h-full">
                    <Card className="bg-card border-border shadow-md h-full flex flex-col justify-center items-center p-8 md:p-12 text-center rounded-md transition-all duration-500 hover:shadow-lg">
                      <CardContent className="p-0 flex flex-col items-center gap-8">
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-6 fill-secondary text-secondary" strokeWidth={1.5} />)}
                        </div>
                        
                        <blockquote className="space-y-2">
                          <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl leading-relaxed text-foreground">
                            "{testimonial.quote}"
                          </p>
                        </blockquote>
                        
                        <div className="flex flex-col items-center gap-1 mt-4">
                          <cite className="font-default font-bold text-lg not-italic text-primary uppercase tracking-widest">
                            {testimonial.author}
                          </cite>
                          <span className="text-sm text-secondary font-semibold uppercase tracking-wider">
                            {testimonial.position}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex size-12 border-primary text-primary hover:bg-primary hover:text-primary-foreground border-[2px] bg-transparent -left-4 md:-left-12 transition-colors duration-300 rounded-md" />
            <CarouselNext className="hidden md:flex size-12 border-primary text-primary hover:bg-primary hover:text-primary-foreground border-[2px] bg-transparent -right-4 md:-right-12 transition-colors duration-300 rounded-md" />
          </Carousel>
        </div>
      </div>
    </section>;
}